/**
 * Query variable extraction and substitution utilities
 * Variables use the {{variableName}} syntax
 */

const VARIABLE_PATTERN = /\{\{\s*(\w+)\s*\}\}/g;

export const PREDEFINED_VARIABLES = ["_user_id", "_company_id"];

/**
 * Extract all variable names from a query string
 * @param query - SQL query with {{variableName}} placeholders
 * @returns Array of unique variable names (without braces)
 */
export function extractVariables(query: string): string[] {
  const matches = query.matchAll(VARIABLE_PATTERN);
  const variables = new Set<string>();
  for (const match of matches) {
    variables.add(match[1]);
  }
  return Array.from(variables);
}

/**
 * Substitute variable placeholders with actual values
 * @param query - SQL query with {{variableName}} placeholders
 * @param values - Record mapping variable names to their values
 * @returns Query with variables substituted
 */
export function substituteVariables(
  query: string,
  values: Record<string, string>
): string {
  return query.replace(VARIABLE_PATTERN, (match, varName) => {
    if (varName in values) {
      // Escape single quotes in values to prevent SQL injection
      const escapedValue = values[varName].replace(/'/g, "''");
      return escapedValue;
    }
    return match; // Keep placeholder if no value provided
  });
}

/**
 * Validate that all required variables have been provided
 * @param requiredVariables - List of required variable names
 * @param providedValues - Record of provided variable values
 * @returns Object with valid flag and array of missing variable names
 */
export function validateVariables(
  requiredVariables: string[],
  providedValues: Record<string, string>
): { valid: boolean; missing: string[] } {
  const missing = requiredVariables.filter(
    (varName) =>
      !PREDEFINED_VARIABLES.includes(varName) &&
      (!(varName in providedValues) || providedValues[varName] === "")
  );
  return {
    valid: missing.length === 0,
    missing,
  };
}

/**
 * Parse URL search params to extract variable values
 * @param searchParams - URL search params
 * @param variables - List of variable names to extract
 * @returns Record of variable name to value (only includes found values)
 */
export function extractVariablesFromUrl(
  searchParams: URLSearchParams,
  variables: string[]
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const varName of variables) {
    const value = searchParams.get(varName);
    if (value !== null) {
      values[varName] = value;
    }
  }
  return values;
}
